import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    FormControl,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CheckIcon from "@mui/icons-material/Check";
import axios from "axios";
import { MONO_FONT, Swatch, formatLab, markedInLabel, shortSampleId } from "./ManualPicksPanel";

const SEARCH_DEBOUNCE_MS = 250;
const EXPLORATION_PAGE_SIZE = 200;

/** "P 40 W · 500 mm/s · 100 kHz · 200 ns · 2 passes · 5 lines / 4 µm": the
 *  design space of a candidate, compact, for the row's second line. */
export function designSummary(design) {
    if (!design || typeof design !== "object") return null;
    const parts = [];
    if (design.power !== undefined) parts.push(`P ${design.power}`);
    if (design.speed !== undefined) parts.push(`${design.speed} mm/s`);
    if (design.frequency !== undefined) parts.push(`${Number(design.frequency) / 1000} kHz`);
    if (design.pulse_duration !== undefined) parts.push(`${design.pulse_duration} pulse`);
    if (design.pass_count !== undefined) parts.push(`${design.pass_count} pass${Number(design.pass_count) === 1 ? "" : "es"}`);
    if (design.line_count !== undefined) parts.push(`${design.line_count} lines`);
    if (design.hatching !== undefined) parts.push(`${(Number(design.hatching) * 1000).toFixed(1)} µm hatch`);
    return parts.length ? parts.join(" · ") : null;
}

/** "L*a*b* 52.3 / 12.0 / -8.4 · 48.0 µm": colour and width of a row. */
export function measurementSummary(row) {
    return [
        `L*a*b* ${formatLab(row?.lab)}`,
        row?.width_mm !== null && row?.width_mm !== undefined
            ? `${(Number(row.width_mm) * 1000).toFixed(1)} µm`
            : null,
    ].filter(Boolean).join(" · ");
}

/**
 * Picks the prepass sample of a new exploration: an exploration of the same
 * laser and substrate, then one of its samples (searchable by ID). The rows
 * come from api/exploration/prepass/candidates, which only lists samples with
 * a measured cluster width and a colour reading. `value` is the chosen row
 * (or null); `onChange` receives the row or null.
 */
export default function PrepassSamplePicker({ laserId, materialId, value, onChange }) {
    const [explorations, setExplorations] = useState([]);
    const [explorationsLoading, setExplorationsLoading] = useState(false);
    const [explorationsError, setExplorationsError] = useState(null);
    const [explorationId, setExplorationId] = useState("");
    const [query, setQuery] = useState("");
    const [debouncedQuery, setDebouncedQuery] = useState("");
    const [results, setResults] = useState([]);
    const [total, setTotal] = useState(0);
    const [searching, setSearching] = useState(false);
    const [searchError, setSearchError] = useState(null);

    useEffect(() => {
        if (!laserId || !materialId) {
            setExplorations([]);
            return undefined;
        }
        let cancelled = false;
        setExplorationsLoading(true);
        setExplorationsError(null);
        axios
            .get(
                `api/exploration/data?page=0&per_page=${EXPLORATION_PAGE_SIZE}&laser=${laserId}&material=${materialId}&sort_by=date&sort_order=desc`
            )
            .then((res) => {
                if (!cancelled) setExplorations(res.data?.data || []);
            })
            .catch((err) => {
                if (!cancelled) setExplorationsError(err?.response?.data?.error || "Could not load the explorations.");
            })
            .finally(() => {
                if (!cancelled) setExplorationsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [laserId, materialId]);

    useEffect(() => {
        const handle = setTimeout(() => setDebouncedQuery(query.trim()), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(handle);
    }, [query]);

    // Without a chosen exploration the search runs over every exploration of
    // the laser and substrate, so a sample can be found by its ID alone.
    const scope = explorationId
        ? `exploration=${explorationId}`
        : `laser=${laserId}&material=${materialId}`;

    useEffect(() => {
        if (!explorationId && (!laserId || !materialId)) {
            setResults([]);
            setTotal(0);
            return undefined;
        }
        let cancelled = false;
        setSearching(true);
        setSearchError(null);
        axios
            .get(`api/exploration/prepass/candidates?${scope}&query=${encodeURIComponent(debouncedQuery)}`)
            .then((res) => {
                if (cancelled) return;
                setResults(res.data?.results || []);
                setTotal(Number(res.data?.total) || 0);
            })
            .catch((err) => {
                if (!cancelled) setSearchError(err?.response?.data?.error || "Could not load the samples.");
            })
            .finally(() => {
                if (!cancelled) setSearching(false);
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scope, debouncedQuery]);

    const summary = useMemo(() => {
        if (!explorationId && (!laserId || !materialId)) return null;
        if (searching && !results.length) return null;
        if (!total) {
            if (debouncedQuery) return "No sample matches this ID.";
            return explorationId
                ? "This exploration has no usable sample (measured width and colour)."
                : "No usable sample (measured width and colour) on this laser and substrate.";
        }
        if (total > results.length) return `Showing ${results.length} of ${total} samples — type part of a sample ID to narrow the list.`;
        return `${total} sample${total === 1 ? "" : "s"}`;
    }, [explorationId, laserId, materialId, searching, results.length, total, debouncedQuery]);

    return (
        <Stack spacing={1.5} data-testid="prepass-picker">
            {value ? (
                <Box
                    data-testid="prepass-selected"
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        p: 1.25,
                        borderRadius: 2,
                        border: "1px solid rgba(22, 163, 74, 0.45)",
                        bgcolor: "rgba(22, 163, 74, 0.06)",
                    }}
                >
                    <Swatch rgb={value.rgb} size={36} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontFamily: MONO_FONT, fontSize: 13.5, fontWeight: 600, wordBreak: "break-all" }}>
                            {value.sample_id}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.4 }}>
                            {[value.exploration_name, markedInLabel(value.marked_in)].filter(Boolean).join(" · ")}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.4, fontVariantNumeric: "tabular-nums" }}>
                            {[measurementSummary(value), designSummary(value.design)].filter(Boolean).join(" · ")}
                        </Typography>
                    </Box>
                    <Button size="small" variant="outlined" onClick={() => onChange(null)} sx={{ textTransform: "none", flexShrink: 0 }}>
                        Clear
                    </Button>
                </Box>
            ) : null}

            <FormControl size="small" fullWidth disabled={explorationsLoading || !explorations.length}>
                <InputLabel id="prepass-exploration-label">Exploration</InputLabel>
                <Select
                    labelId="prepass-exploration-label"
                    label="Exploration"
                    value={explorationId}
                    displayEmpty
                    onChange={(event) => {
                        setExplorationId(event.target.value);
                    }}
                    inputProps={{ "data-testid": "prepass-exploration-select" }}
                >
                    <MenuItem value="">All explorations of this laser and substrate</MenuItem>
                    {explorations.map((row) => (
                        <MenuItem key={row.id} value={row.id}>
                            {row.name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            {explorationsLoading ? (
                <Typography variant="caption" color="text.secondary">Loading the explorations…</Typography>
            ) : null}
            {explorationsError ? (
                <Typography variant="caption" color="error">{explorationsError}</Typography>
            ) : null}
            {!explorationsLoading && !explorationsError && !explorations.length ? (
                <Typography variant="caption" color="text.secondary">
                    No exploration exists for this laser and substrate yet.
                </Typography>
            ) : null}

            {explorationId || (laserId && materialId) ? (
                <TextField
                    size="small"
                    fullWidth
                    label="Sample ID"
                    placeholder={explorationId ? "Type part of a sample ID" : "Type part of a sample ID to search all explorations"}
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    inputProps={{ "data-testid": "prepass-search" }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action" />
                            </InputAdornment>
                        ),
                        endAdornment: searching ? (
                            <InputAdornment position="end">
                                <CircularProgress size={18} />
                            </InputAdornment>
                        ) : null,
                    }}
                />
            ) : null}
            {searchError ? (
                <Typography variant="caption" color="error">{searchError}</Typography>
            ) : null}
            {summary ? (
                <Typography variant="body2" color="text.secondary" data-testid="prepass-summary">
                    {summary}
                </Typography>
            ) : null}

            {results.length ? (
                <Stack spacing={1} component="ul" sx={{ listStyle: "none", p: 0, m: 0, maxHeight: 360, overflowY: "auto" }}>
                    {results.map((row) => {
                        const selected = value?.sample_id === row.sample_id;
                        return (
                            <Box
                                component="li"
                                key={row.sample_id}
                                data-testid="prepass-candidate"
                                onClick={() => onChange(selected ? null : row)}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                    p: 1.25,
                                    borderRadius: 2,
                                    border: "1px solid",
                                    borderColor: selected ? "rgba(22, 163, 74, 0.45)" : "rgba(15, 23, 42, 0.1)",
                                    bgcolor: selected ? "rgba(22, 163, 74, 0.06)" : "background.paper",
                                    cursor: "pointer",
                                    "&:hover": { borderColor: selected ? "rgba(22, 163, 74, 0.7)" : "rgba(15, 23, 42, 0.28)" },
                                }}
                            >
                                <Swatch rgb={row.rgb} size={36} />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography sx={{ fontFamily: MONO_FONT, fontSize: 13.5, fontWeight: 600, wordBreak: "break-all" }}>
                                        {row.sample_id}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.4 }}>
                                        {[
                                            explorationId ? null : row.exploration_name,
                                            markedInLabel(row.marked_in),
                                            measurementSummary(row),
                                        ].filter(Boolean).join(" · ")}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.4 }}>
                                        {designSummary(row.design)}
                                    </Typography>
                                </Box>
                                {selected ? (
                                    <Chip size="small" color="success" icon={<CheckIcon />} label="Selected" />
                                ) : (
                                    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                                        {shortSampleId(row.sample_id)}
                                    </Typography>
                                )}
                            </Box>
                        );
                    })}
                </Stack>
            ) : null}
        </Stack>
    );
}
